import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { authClient } from "../../utils/auth-client";
import { config } from "../../utils/config";
import {
  addBreadcrumb,
  clearUserContext,
  setUserContext,
} from "../../utils/sentry";

export interface EmailPasswordRequest {
  email: string;
  password: string;
}

interface AuthContextType {
  authenticate: (
    provider: "apple" | "google",
    options?: {
      callbackURL?: string;
      newUserCallbackURL?: string;
      errorCallbackURL?: string;
    },
  ) => Promise<void>;
  signOut: () => Promise<void>;
  signUpWithPassword: (
    request: EmailPasswordRequest & {
      skipNavigation?: boolean;
      callbackURL?: string;
    },
  ) => Promise<any>;
  authenticateWithPassword: (
    request: EmailPasswordRequest & { flow?: "login" | "get-started" },
  ) => Promise<any>;
  forgetPassword: (request: { email: string }) => Promise<any>;
  changePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<any>;
  unlinkAccount: (providerId: string, accountId: string) => Promise<any>;
  user: string | null;
  loading: boolean;
  userSignupProgress?: any;
  updateSignupProgress?: (progress: any) => void;
  newAccount: boolean;
  freshSession: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

class AuthError extends Error {
  code?: string;
  status?: number;
  statusText?: string;

  constructor(error: {
    message: string;
    code?: string;
    status?: number;
    statusText?: string;
  }) {
    super(error.message);
    this.name = "AuthError";
    this.code = error.code;
    this.status = error.status;
    this.statusText = error.statusText;
  }
}

// Routes that don't require authentication
const PUBLIC_ROUTES = [
  "/auth",
  "/forgot-password",
  "/onboarding/verify-email",
  "/purchase",
  "/v2/static/email-verified",
  "/checkout",
];

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: session,
    isPending: loading,
    refetch: refetchSession,
  } = authClient.useSession();

  const user = useMemo(() => {
    console.log("🔍 AuthProvider session state:", session);
    console.log("🔍 AuthProvider user:", session?.user?.id ?? null);

    // Update Sentry user context when user changes
    if (session?.user) {
      setUserContext({
        id: session.user.id,
        email: session.user.email,
      });
      addBreadcrumb("User session updated", "auth", {
        userId: session.user.id,
        // Only include email in development for privacy
        ...(import.meta.env.VITE_NODE_ENV !== "production" && {
          email: session.user.email,
        }),
      });
    } else {
      clearUserContext();
      addBreadcrumb("User session cleared", "auth");
    }

    return session?.user.id ?? null;
  }, [session]);

  // Add additional protection: don't redirect until we've had a chance to check session
  const [hasCheckedSession, setHasCheckedSession] = useState(false);

  useEffect(() => {
    if (!loading) {
      // Small delay to ensure session check is complete
      const timer = setTimeout(() => {
        setHasCheckedSession(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // Authentication redirect logic
  useEffect(() => {
    // Don't redirect while loading, if already authenticated, or before session check is complete
    if (loading || user || !hasCheckedSession) {
      return;
    }

    const currentPath = location.pathname;
    const isPublicRoute = PUBLIC_ROUTES.some((route) =>
      currentPath.startsWith(route),
    );
    const isCheckoutRoute =
      currentPath.startsWith("/checkout") || currentPath.startsWith("/stripe");

    // Don't redirect if already on a public route, checkout route, or stripe route
    if (isPublicRoute || isCheckoutRoute) {
      return;
    }

    // Redirect to purchase flow for unauthenticated users
    console.debug(
      "🔄 Redirecting users to /purchase flow when they click on a route not defined in PUBLIC_ROUTES",
    );
    navigate("/purchase", { replace: true });
  }, [
    user,
    loading,
    hasCheckedSession,
    location.pathname,
    location.search,
    navigate,
  ]);

  const authenticate = async (
    provider: "apple" | "google",
    options?: {
      callbackURL?: string;
      newUserCallbackURL?: string;
      errorCallbackURL?: string;
    },
  ) => {
    // For web, we'll use the OAuth flow instead of ID tokens
    const webOrigin = config.webUrl.replace(/\/$/, "");
    const result = await authClient.signIn.social({
      provider,
      disableRedirect: false,
      // Use absolute URLs so the auth server redirects back to the web app origin (http://localhost:5173 in dev)
      callbackURL: options?.callbackURL || `${webOrigin}/`,
      errorCallbackURL: options?.errorCallbackURL || `${webOrigin}/auth/error`,
      newUserCallbackURL: options?.newUserCallbackURL || `${webOrigin}/`, // go home, web does not have an onboarding route
    });

    if (result.error) {
      throw new AuthError({
        message: result.error.message || "Authentication failed",
        code: result.error.code,
        status: result.error.status,
        statusText: result.error.statusText,
      });
    }
  };

  const signOut = async () => {
    try {
      await authClient.signOut();
      navigate("/");
    } catch (error) {
      console.error(`Sign out error: ${error}`);
    }
  };

  const forgetPassword = async ({ email }: { email: string }) => {
    const res = await authClient.forgetPassword({
      email,
      redirectTo: "/v2/static/reset-password",
    });
    console.debug("🔄 Web auth: forgotPassword() res:", res);
    return res;
  };

  const signUpWithPassword = async ({
    email,
    password,
    skipNavigation = false,
    callbackURL,
  }: EmailPasswordRequest & {
    skipNavigation?: boolean;
    callbackURL?: string;
  }) => {
    console.debug(
      "🔄 Web auth: signUpWithPassword() callbackURL:",
      callbackURL,
    );

    const res = await authClient.signUp.email({
      email,
      password,
      name: "",
      callbackURL: callbackURL,
    });
    if (res.error) {
      throw new AuthError({
        message: res.error.message || "Authentication failed",
        code: res.error.code,
        status: res.error.status,
        statusText: res.error.statusText,
      });
    }

    // Only navigate if not skipping navigation (for custom flows)
    if (!skipNavigation) {
      navigate("/onboarding/verify-email", { state: { email, password } });
    }

    console.debug("🔄 Web auth: signUpWithPassword() res:", res);
    return res;
  };

  const authenticateWithPassword = async ({
    email,
    password,
    flow = "login",
  }: EmailPasswordRequest & { flow?: "login" | "get-started" }) => {
    const res = await authClient.signIn.email({
      email,
      password,
    });
    console.debug("🔄 Web auth: authenticateWithPassword() res:", res);

    if (res.error) {
      if (res.error.code === "EMAIL_NOT_VERIFIED") {
        return {
          error: {
            message:
              "Email not verified. Please verify your email before signing in.",
            code: res.error.code,
            status: res.error.status,
            statusText: res.error.statusText,
          },
        };
      } else {
        throw new AuthError({
          message: res.error.message || "Authentication failed",
          code: res.error.code,
          status: res.error.status,
          statusText: res.error.statusText,
        });
      }
    } else {
      console.debug("🍪 Document cookies before refetch:", document.cookie);

      // Force refresh the session data in React state
      await refetchSession();
      await authClient.getSession();

      console.debug("🍪 Document cookies after refetch:", document.cookie);

      if (flow === "get-started") {
        navigate("/onboarding");
      }
    }
    return res;
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string,
  ) => {
    return authClient.changePassword({
      currentPassword,
      newPassword,
    });
  };

  const unlinkAccount = async (providerId: string, accountId: string) => {
    return authClient.unlinkAccount({
      providerId,
      accountId,
    });
  };

  return (
    <AuthContext.Provider
      value={{
        authenticate,
        signOut,
        signUpWithPassword,
        authenticateWithPassword,
        forgetPassword,
        changePassword,
        unlinkAccount,
        user,
        loading: loading || !hasCheckedSession,
        userSignupProgress: undefined,
        updateSignupProgress: undefined,
        newAccount: false,
        freshSession: false,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
